<template>
  <div class="dashboard-pages">
    <v-layout row mb-2 justify-center>
      <v-flex xs3 pa-2 ma-2 green lighten-3 v-show="showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Debit') }}:
          <strong>{{ balanceDetails.debit | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>

      <v-flex xs3 pa-2 ma-2 red lighten-3 @click="showBalanceDetail = !showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Balance') }}:
          <strong>{{ balanceDetails.balance | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>

      <v-flex xs3 pa-2 ma-2 yellow lighten-3 v-show="showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Credit') }}:
          <strong>{{ balanceDetails.credit | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>
    </v-layout>

    <v-card pa-3>
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.account_payable_id }}</td>-->
          <td>{{ props.item.vender_name }}</td>
          <td>{{ props.item.total_debit | currency }}</td>
          <td>{{ props.item.total_balance | currency }}</td>
          <td>{{ props.item.total_credit | currency }}</td>
          <td>
            <v-tooltip top>
              <v-chip
                slot="activator"
                label
                :color="
                  props.item.account_status === 'Paid'
                    ? 'success'
                    : props.item.account_status === 'Paid Partially'
                    ? 'warning'
                    : 'error'
                "
                text-color="white"
                class="no-select"
              >
                <v-icon>{{
                  props.item.account_status === 'Paid' || props.item.account_status === 'Paid Partially'
                    ? 'attach_money'
                    : 'money_off'
                }}</v-icon>
              </v-chip>
              <span>{{
                props.item.account_status === 'Paid'
                  ? $t('Paid')
                  : props.item.account_status === 'Paid Partially'
                  ? $t('Paid Partially')
                  : $t('Not Paid')
              }}</span>
            </v-tooltip>
          </td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['account_payable_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item.account_payable_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Account Payable Information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['payable_statement_show'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="statements(props.item)">
                    <v-icon>assignment</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('Statements') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Statements Account Payable Information`) }}</span>
                </v-tooltip>

                <v-tooltip v-if="hasPermissions(['payable_payment_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="payments(props.item)">
                    <v-icon>payment</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('Payments') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Account Payments Information`) }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <view-account-payable :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    viewAccountPayable: () => import('./viewAccountPayable.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue')
  },

  data() {
    return {
      systemCurrency: localStorage.systemCurrency,
      dialogs: {
        viewDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'account_payable_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'account_payable_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Supplier'), value: 'vender_name' },
        {
          text: `${this.$t('Total Debit')} (${localStorage.systemCurrency})`,
          value: 'total_debit'
        },
        {
          text: `${this.$t('Total Balance')} (${localStorage.systemCurrency})`,
          value: 'total_balance'
        },
        {
          text: `${this.$t('Total Credit')} (${localStorage.systemCurrency})`,
          value: 'total_credit'
        },
        { text: this.$t('Account Status'), value: 'account_status' }
      ],
      allowGettingData: true,
      showBalanceDetail: false
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Account Payable'),
      icon: 'attach_money'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }
  },

  computed: {
    ...mapGetters({
      resultData: 'accountPayable/getAccountPayableList',
      balanceDetails: 'accountPayable/getAccountPayableBalanceDetails'
    })
  },

  watch: {
    pagination: {
      immediate: false,
      deep: false,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'accountPayable/fetchAccountPayableList',
      fetchViewInfo: 'accountPayable/fetchAccountPayableInfo',
      fetchBalanceDetails: 'accountPayable/fetchAccountPayableBalanceDetails'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions(['account_payable_show', 'payable_statement_show'])
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('account_payable_index', {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        ...this.pagination,
        search: this.search
      }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })

      this.fetchBalanceDetails()
    },

    resetDataTable() {
      this.allowGettingData = true

      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchViewInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    },

    statements(accountPayable) {
      this.$router.push({
        name: 'accounting-account-payable-statements',
        query: {
          account_payable_id: accountPayable.account_payable_id,
          name: accountPayable.vender_name
        }
      })
    },

    payments(accountPayable) {
      this.$router.push({
        name: 'accounting-account-payable-payments',
        query: {
          account_payable_id: accountPayable.account_payable_id,
          name: accountPayable.vender_name,
          commission: false
        }
      })
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
